import { useEffect, useRef, useState } from "react";
import { scrollToId } from "@/lib/event";

const LINKS = [
  ["rsvp", "RSVP"],
  ["rules", "Rules"],
  ["memories", "Photos"],
];

function currentSection() {
  const nav = document.querySelector("[data-testid='section-nav']");
  const navBottom = nav ? nav.getBoundingClientRect().bottom : 0;
  const probe = navBottom + (window.innerHeight - navBottom) * 0.35;
  const doc = document.documentElement;
  if (window.scrollY + window.innerHeight >= doc.scrollHeight - 4) return "memories";
  const photos = document.getElementById("memories");
  if (photos && photos.getBoundingClientRect().top <= probe) return "memories";
  const rules = document.getElementById("rules")?.getBoundingClientRect();
  if (rules && rules.top <= probe && rules.bottom > probe) return "rules";
  return "rsvp";
}

function useActiveSection() {
  const [active, setActive] = useState("rsvp");
  const lock = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    let frame = 0;
    const release = () => {
      lock.current = null;
      setActive(currentSection());
    };
    const onScroll = () => {
      if (lock.current) {
        clearTimeout(timer.current);
        timer.current = setTimeout(release, 180);
        return;
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setActive(currentSection()));
    };
    const interrupt = () => {
      if (lock.current) release();
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    window.addEventListener("wheel", interrupt, { passive: true });
    window.addEventListener("touchstart", interrupt, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer.current);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("wheel", interrupt);
      window.removeEventListener("touchstart", interrupt);
    };
  }, []);

  const go = (id) => {
    lock.current = id;
    setActive(id);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      lock.current = null;
      setActive(currentSection());
    }, 1500);
    scrollToId(id);
  };
  return [active, go];
}

export default function SectionNav() {
  const [active, go] = useActiveSection();
  return (
    <nav aria-label="Sections" className="sticky top-0 z-30 border-y-[2.5px] border-[var(--navy)] bg-[var(--cream)]" data-testid="section-nav">
      <ul className="mx-auto flex max-w-7xl items-center justify-center gap-3 px-3 py-2 sm:gap-5">
        {LINKS.map(([id, label]) => {
          const on = active === id;
          return (
            <li key={id}>
              <button
                type="button"
                onClick={() => go(id)}
                aria-current={on ? "true" : undefined}
                className={`min-h-[40px] whitespace-nowrap rounded-full border-2 px-5 text-sm font-extrabold transition-colors duration-200 sm:px-6 ${on ? "border-[var(--navy)] bg-[var(--pink)] text-[var(--cream)]" : "border-transparent [@media(hover:hover)]:hover:border-[var(--navy)] [@media(hover:hover)]:hover:bg-[var(--yellow)]"}`}
                data-testid={`nav-${id}`}
                data-active={on ? "true" : "false"}
              >
                {label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
